import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import { green } from '@mui/material/colors';
import InputAdornment from '@mui/material/InputAdornment';
import { IOSSwitch } from '../common/IosSwitch';
import { VEHICLE_TYPE, CONDITION, FUEL_TYPE_MAP, COLOR } from '../docs/constants';
import { DECODE_VIN } from '../../redux-state/fees/types';
import { selectVehicleInfo, selectLocationInfo } from '../../redux-state/selectors';
import { UPDATE_VEHICLE_INFO } from '../../redux-state/forms/types';
import CurrencyTextField from '../common/CurrencyField'
import { getRequiredVehicleSpecFields } from '../utility/stateRequirements';


function VehicleInfo({ errors }) {
  const [isOptionalFieldVisible, setIsOptionalFieldVisible] = useState(false);
  const vehicleInfo = useSelector(selectVehicleInfo);
  const locationInfo = useSelector(selectLocationInfo);
  const [localError, setLocalError] = useState('');
  const dispatch = useDispatch();

  // Per-state GVW / Weight / MPG requirements, using the same tables the
  // bot checks (utility/stateRequirements.js, ported from
  // state_requirements.py). Nothing here is required until a state that
  // requires it is selected, and where the ATC guide scopes the
  // requirement to particular vehicle types, only for those types.
  const requiredSpecs = getRequiredVehicleSpecFields(
    'docs',
    locationInfo?.state,
    vehicleInfo?.vehicleType
  );
  // Weight, GVW and MPG live inside the collapsed "Optional Information"
  // panel, so a state-driven requirement would otherwise be invisible —
  // and unfillable. Reveal the panel when one becomes active, or when
  // submit flags one of them. The user can still collapse it again;
  // submit-time validation is what enforces the requirement, and
  // scrollToFirstError needs the field mounted to be able to reach it,
  // which is why this reveals rather than merely flagging.
  const mustShowSpecFields =
    requiredSpecs.gvw ||
    requiredSpecs.weight ||
    requiredSpecs.mpg ||
    !!errors?.gvw ||
    !!errors?.vehicleWeight ||
    !!errors?.mpg;

  useEffect(() => {
    if (mustShowSpecFields) {
      setIsOptionalFieldVisible(true);
    }
  }, [mustShowSpecFields]);

  const validateVIN = (vin) => /^[A-HJ-NPR-Z0-9]{17}$/.test(vin.trim());

  const decodeVinCode = () => {
    if (!validateVIN(vehicleInfo.vinCode)) {
      setLocalError('Please enter a valid 17-character VIN');
      return;
    }

    setLocalError('');
    dispatch({
      type: DECODE_VIN,
      payload: { vin: vehicleInfo.vinCode } });
  };

  const handleChange = (key, value) => {
     dispatch({
        type: UPDATE_VEHICLE_INFO,
        payload: { key, value},
      });
  }

  return (
    <div className="flex flex-col w-full">
      <div className="p-4 rounded-lg bg-white text-gray-700">
        <div className="text-lg font-bold">
          Vehicle Information
        </div>
        <div className="flex flex-wrap justify-between mt-2 text-sm">
          <div className="text-gray-600">
            Add correct vehicle details
          </div>
        </div>

        <div className="flex flex-col md:flex-row mt-4 gap-4">
          <TextField
            required
            label="VIN Number"
            variant="outlined"
            fullWidth
            value={vehicleInfo?.vinCode}
            onChange={(e) => handleChange('vinCode', e.target.value)}
            InputProps={{ style: { borderRadius: '10px' } }}
            error={!!errors.vinCode || !!localError}
            helperText={errors.vinCode || localError}
          />
          <Button
            onClick={decodeVinCode}
            variant="outlined"
            sx={{
              minWidth: '150px',
              height: '55px',
              color: '#229954',
              borderRadius: '10px',
              borderColor: '#229954',
              '&:hover': { backgroundColor: green[900], color: '#fff' },
            }}
          >
            Decode VIN
          </Button>
        </div>

        <div className="flex flex-col md:flex-row mt-4 gap-4">
          <FormControl fullWidth required error={!!errors.vehicleType}>
            <InputLabel>Vehicle Type</InputLabel>
            <Select
              label="Vehicle Type"
              value={vehicleInfo?.vehicleType}
              onChange={(e) => handleChange('vehicleType', e.target.value)}
            >
              {Object.keys(VEHICLE_TYPE).map((key, index) => (
                <MenuItem
                  key={key}
                  value={VEHICLE_TYPE[key]}
                  className={key === 'Multipurpose Passenger Vehicle' ? 'hidden-important' : ''}
                >
                  {key}
                </MenuItem>
              ))}
            </Select>
            {errors.vehicleType && (
              <div style={{ color: 'red', fontSize: '0.75rem' }}>{errors.vehicleType}</div>
            )}
          </FormControl>

          <FormControl fullWidth required error={!!errors.condition}>
            <InputLabel>Condition</InputLabel>
            <Select
              label="Condition"
              value={vehicleInfo?.condition}
              onChange={(e) => handleChange('condition', e.target.value)}
            >
              {Object.keys(CONDITION).map((key, index) => (
                <MenuItem key={index} value={key}>{CONDITION[key]}</MenuItem>
              ))}
            </Select>
            {errors.condition && (
              <div style={{ color: 'red', fontSize: '0.75rem' }}>{errors.condition}</div>
            )}
          </FormControl>
        </div>

        <div className="flex flex-col md:flex-row mt-4 gap-4">
          <TextField
            label="Model Year"
            variant="outlined"
            fullWidth
            value={vehicleInfo?.modelYear}
            onChange={(e) => handleChange('modelYear', e.target.value)}
            InputProps={{ style: { borderRadius: '10px' } }}
          />
          <TextField
            label="Make"
            variant="outlined"
            fullWidth
            value={vehicleInfo?.make || ''}
            onChange={(e) => handleChange('make', e.target.value)}
            InputProps={{ style: { borderRadius: '10px' } }}
          />
        </div>

        <div className="flex flex-col md:flex-row mt-4 gap-4">
           <TextField
              label="Model"
              variant="outlined"
              fullWidth
              value={vehicleInfo?.model || ''}
              onChange={(e) => handleChange('model', e.target.value)}
              InputProps={{ style: { borderRadius: '10px' } }}
            />

          <FormControl fullWidth>
            <InputLabel>Body Style</InputLabel>
            <Select
              label="Body Style"
              value={vehicleInfo?.bodyStyle}
              onChange={(e) => handleChange('bodyStyle', e.target.value)}
            >
              {["Convertible", "Coupe", "Hatchback", "Minivan", "Passenger Sedan", "Sport Utility Vehicle", "Sedan", "Pickup Truck", "Van", "Truck", "Motorcycle", "Recreational Vehicle", "Wagon"].map((option, index) => (
                <MenuItem key={index} value={option}>{option}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>

        <div className="flex flex-col md:flex-row mt-4 gap-4">
          <TextField
            label="Number of Axles"
            type="number"
            variant="outlined"
            fullWidth
            value={vehicleInfo?.numberOfAxles}
            onChange={(e) => handleChange('numberOfAxles', e.target.value)}
            InputProps={{ style: { borderRadius: '10px' } }}
          />
          <TextField
            label="Number of Tires"
            type="number"
            variant="outlined"
            fullWidth
            value={vehicleInfo?.numberOfTires}
            onChange={(e) => handleChange('numberOfTires', e.target.value)}
            InputProps={{ style: { borderRadius: '10px' } }}
          />
        </div>

        <div className="flex flex-col md:flex-row mt-4 gap-4">
          <FormControl fullWidth>
            <InputLabel>Fuel Type</InputLabel>
            <Select
              label="Fuel Type"
              value={vehicleInfo?.fuelType}
              onChange={(e) => handleChange('fuelType', e.target.value)}
            >
              {Object.keys(FUEL_TYPE_MAP).map((key, index) => (
                <MenuItem key={index} value={key}>{FUEL_TYPE_MAP[key]}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Cylinders</InputLabel>
            <Select
              label="Cylinders"
              type="number"
              value={vehicleInfo?.cylinders}
              onChange={(e) => handleChange('cylinders', e.target.value)}
            >
              {['3', '4', '6', '8', '10', '12'].map((option, index) => (
                <MenuItem key={index} value={option}>{option}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>

        <div className='flex flex-col md:flex-row mt-4 gap-4'>
          <TextField
            label="Registration Months"
            type="number"
            variant="outlined"
            fullWidth
            value={vehicleInfo?.registrationMonths}
            onChange={(e) => handleChange('registrationMonths', e.target.value)}
            InputProps={{ style: { borderRadius: '10px' } }}
          />
        </div>
        <div className="text-gray-600 font-bold mt-4">
          Optional Information
          <FormControlLabel
            control={
              <IOSSwitch
                sx={{ ml: 3 }}
                checked={isOptionalFieldVisible}
                onChange={(e) => setIsOptionalFieldVisible(e.target.checked)}
              />
            }
          />
        </div>

        {isOptionalFieldVisible && (
          <div className="space-y-4 mt-4">
            <div className="flex flex-col md:flex-row gap-4">
              <TextField
                label="Odometer (miles)"
                type="number"
                variant="outlined"
                fullWidth
                value={vehicleInfo?.odometer}
                onChange={(e) => handleChange('odometer', e.target.value)}
                InputProps={{ style: { borderRadius: '10px' } }}
              />
              <TextField
                label="Rim Size (inches)"
                type="number"
                variant="outlined"
                fullWidth
                value={vehicleInfo?.rimSize}
                onChange={(e) => handleChange('rimSize', e.target.value)}
                InputProps={{ style: { borderRadius: '10px' } }}
              />
            </div>

            <div className="flex flex-col md:flex-row gap-4">
              <FormControl fullWidth>
                <InputLabel>Color</InputLabel>
                <Select
                  label="Color"
                  value={vehicleInfo?.color}
                  onChange={(e) => handleChange('color', e.target.value)}
                >
                  {Object.values(COLOR).map((option, index) => (
                    <MenuItem key={index} value={option}>{option}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Weight"
                type="number"
                variant="outlined"
                fullWidth
                required={requiredSpecs.weight}
                value={vehicleInfo?.vehicleWeight}
                onChange={(e) => handleChange('vehicleWeight', e.target.value)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Select
                        value="lbs"
                        variant="standard"
                        disableUnderline
                        sx={{ fontSize: '0.875rem' }}
                      >
                        <MenuItem value="lbs">lbs</MenuItem>
                      </Select>
                    </InputAdornment>
                  ),
                  style: { borderRadius: '10px' },
                }}
                error={!!errors?.vehicleWeight}
                helperText={errors?.vehicleWeight}
              />
            </div>

            <div className="flex flex-col md:flex-row gap-4">
              <TextField
                label="GVW (lbs)"
                variant="outlined"
                fullWidth
                required={requiredSpecs.gvw}
                value={vehicleInfo?.gvw}
                onChange={(e) => handleChange('gvw', e.target.value)}
                InputProps={{ style: { borderRadius: '10px' } }}
                error={!!errors?.gvw}
                helperText={errors?.gvw}
              />
              <TextField
                label="MPG (average)"
                type="number"
                variant="outlined"
                fullWidth
                required={requiredSpecs.mpg}
                value={vehicleInfo?.mpg}
                onChange={(e) => handleChange('mpg', e.target.value)}
                InputProps={{ style: { borderRadius: '10px' } }}
                error={!!errors?.mpg}
                helperText={errors?.mpg}
              />
            </div>

            <div className="flex flex-col md:flex-row gap-4">
              <TextField
                label="Horsepower"
                variant="outlined"
                fullWidth
                value={vehicleInfo?.horsepower}
                onChange={(e) => handleChange('horsepower', e.target.value)}
                InputProps={{ style: { borderRadius: '10px' } }}
              />

              <CurrencyTextField
                label="MSRP"
                name="msrp"
                required
                value={vehicleInfo.msrp ?? ''} // keep controlled
                onChange={(num) =>
                  dispatch({
                    type: 'UPDATE_VEHICLE_INFO',
                    payload: { key: 'msrp', value: num }, // <-- num is a Number or '' when cleared
                  })
                }
                InputProps={{ style: { borderRadius: '10px' } }}
                error={!!errors?.msrp}
                helperText={errors?.msrp}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default VehicleInfo;
